import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BookingSummary from './components/BookingSummary';
import PricingBreakdown from './components/PricingBreakdown';
import GuestInformation from './components/GuestInformation';
import PaymentMethodSelector from './components/PaymentMethodSelector';
import ConfirmationModal from './components/ConfirmationModal';
import { useAuth } from '../../contexts/AuthContext';
import { saveBooking } from '../../services/bookingService';

const TripConfirmationCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [bookingData, setBookingData] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('gcash');
  const [guestInfo, setGuestInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: ''
  });
  const [paymentDetails, setPaymentDetails] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState('');

  useEffect(() => {
    // Get booking data from navigation state or localStorage
    const bookingFromState = location?.state?.booking;
    if (bookingFromState) {
      setBookingData(bookingFromState);
    } else {
      // Fallback mock data for testing
      setBookingData({
        id: 'BK' + Date.now()?.toString()?.slice(-6),
        type: 'accommodation',
        name: 'Boracay Beachfront Resort',
        location: 'Boracay, Aklan',
        description: 'Deluxe ocean view room with balcony and complimentary breakfast',
        image: "https://images.unsplash.com/photo-1624323118407-6356ba4ee473",
        imageAlt: 'Luxury beachfront resort room with ocean view balcony in Boracay',
        checkIn: '2026-03-15',
        checkOut: '2026-03-20',
        guests: 2,
        nights: 5,
        roomType: 'Deluxe Ocean View',
        baseAmount: 20000,
        taxesAndFees: 3000,
        serviceFee: 2000,
        totalAmount: 25000,
        amenities: ['Free WiFi', 'Breakfast Included', 'Pool Access', 'Beach Access'],
        cancellationPolicy: 'Free cancellation until 7 days before check-in. 50% refund for cancellations 3-7 days before. No refund for cancellations within 3 days.',
        contactEmail: 'reservations@boracayresort.ph',
        contactPhone: '+63 917 123 4567'
      });
    }
  }, [location]);

  const handleCompleteBooking = () => {
    // Validate guest information
    if (!guestInfo?.firstName || !guestInfo?.lastName || !guestInfo?.email || !guestInfo?.phone) {
      alert('Please fill in all required guest information');
      return;
    }

    // Validate terms acceptance
    if (!termsAccepted || !policyAccepted) {
      alert('Please accept the terms and cancellation policy');
      return;
    }

    // Validate payment details based on selected method
    if (selectedPaymentMethod === 'gcash' && (!paymentDetails?.gcashNumber || !paymentDetails?.gcashName)) {
      alert('Please provide GCash payment details');
      return;
    }

    if (selectedPaymentMethod === 'credit_card' && (!paymentDetails?.cardNumber || !paymentDetails?.cardName || !paymentDetails?.cardExpiry || !paymentDetails?.cardCvv)) {
      alert('Please provide complete credit card details');
      return;
    }

    setIsProcessing(true);

    // Simulate payment processing
    setTimeout(async () => {
      const confNumber = `CONF-${new Date()?.getFullYear()}-${bookingData?.id}`;
      setConfirmationNumber(confNumber);

      // Save booking to Supabase database
      const completedBooking = {
        ...bookingData,
        confirmationNumber: confNumber,
        paymentMethod: selectedPaymentMethod,
        paymentStatus: selectedPaymentMethod === 'pay_at_hotel' ? 'pending' : 'paid',
        bookingStatus: 'confirmed',
        guestInfo: guestInfo,
        bookingDate: new Date()?.toISOString()
      };

      if (user?.id) {
        await saveBooking(completedBooking, user?.id);
      } else {
        // Fallback to localStorage if not authenticated
        const existingBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
        localStorage.setItem('userBookings', JSON.stringify([...existingBookings, completedBooking]));
      }

      setIsProcessing(false);
      setShowConfirmation(true);
    }, 2500);
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    navigate('/bookings-dashboard');
  };

  if (!bookingData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">No Booking Data Found</h2>
          <p className="text-muted-foreground mb-6">Please select a booking to proceed with checkout.</p>
          <Button onClick={() => navigate('/bookings-dashboard')}>Go to Bookings</Button>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Page Header */}
      <div className="bg-gradient-to-r from-primary to-secondary py-12">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-4 transition-colors">

            <Icon name="ArrowLeft" size={20} />
            <span>Back</span>
          </button>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Trip Confirmation & Checkout</h1>
          <p className="text-white/90">Review your booking details and complete your reservation</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Booking Summary & Guest Info */}
          <div className="lg:col-span-2 space-y-6">
            <BookingSummary booking={bookingData} />
            
            <GuestInformation
              guestInfo={guestInfo}
              onGuestInfoChange={setGuestInfo} />


            {/* Terms and Policies */}
            <div className="bg-card rounded-xl border border-border p-6 space-y-4">
              <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                <Icon name="FileText" size={20} className="text-primary" />
                Terms & Policies
              </h3>
              
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e?.target?.checked)}
                    className="mt-1 h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-ring" />

                  <span className="text-sm text-foreground group-hover:text-primary transition-colors">
                    I agree to the <span className="font-semibold text-primary">Terms and Conditions</span> and <span className="font-semibold text-primary">Privacy Policy</span>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={policyAccepted}
                    onChange={(e) => setPolicyAccepted(e?.target?.checked)}
                    className="mt-1 h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-ring" />

                  <span className="text-sm text-foreground group-hover:text-primary transition-colors">
                    I have read and understood the <span className="font-semibold text-primary">Cancellation Policy</span>
                  </span>
                </label>
              </div>

              {/* Cancellation Policy Details */}
              <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">Cancellation Policy</h4>
                    <p className="text-xs text-muted-foreground">{bookingData?.cancellationPolicy}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Payment & Pricing */}
          <div className="space-y-6">
            <PricingBreakdown booking={bookingData} />
            
            <PaymentMethodSelector
              selectedMethod={selectedPaymentMethod}
              onMethodChange={setSelectedPaymentMethod}
              paymentDetails={paymentDetails}
              onPaymentDetailsChange={setPaymentDetails} />


            {/* Complete Booking Button */}
            <Button
              onClick={handleCompleteBooking}
              loading={isProcessing}
              disabled={isProcessing || !termsAccepted || !policyAccepted}
              fullWidth
              size="lg"
              className="shadow-lg">

              {isProcessing ? 'Processing...' : 'Complete Booking'}
            </Button>

            {/* Security Badge */}
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="Lock" size={16} />
              <span>Secure payment processing</span>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={handleConfirmationClose}
        confirmationNumber={confirmationNumber}
        booking={bookingData}
        guestInfo={guestInfo} />

    </div>);

};

export default TripConfirmationCheckout;